import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const JURISDICTIONS = [
  { id: "municipal", label: "Municipal", desc: "City clerk, mayor's office, municipal departments" },
  { id: "county", label: "County", desc: "County recorder, sheriff, county departments" },
  { id: "state", label: "State agency", desc: "State-level executive, regulatory, or judicial body" },
  { id: "federal", label: "Federal", desc: "Federal department, agency, or commission" },
];

const LAWS = [
  { id: "FOIA", label: "Federal FOIA (5 U.S.C. § 552)", days: 20 },
  { id: "CPRA", label: "California Public Records Act", days: 10 },
  { id: "NY-FOIL", label: "New York Freedom of Information Law", days: 5 },
  { id: "TX-PIA", label: "Texas Public Information Act", days: 10 },
  { id: "FL-Sunshine", label: "Florida Sunshine Law", days: 10 },
  { id: "OTHER", label: "Other state public records statute", days: 10 },
];

export default function Onboarding() {
  const { checkAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [jurisdiction, setJurisdiction] = useState("municipal");
  const [law, setLaw] = useState("FOIA");
  const [days, setDays] = useState(20);
  const [inviteEmails, setInviteEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const emails = inviteEmails.split(/[,\s\n]+/).map((s) => s.trim()).filter(Boolean);
      await api.post("/onboarding", {
        jurisdiction_type: jurisdiction,
        applicable_law: law,
        response_window_days: Number(days),
        invite_emails: emails,
      });
      await checkAuth();
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="mb-8 flex items-center gap-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-7 h-7 flex items-center justify-center text-xs font-mono ${step >= s ? "bg-[#0B1B3D] text-white" : "bg-white border border-slate-300 text-slate-500"}`}>
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-px ${step > s ? "bg-[#0B1B3D]" : "bg-slate-300"}`} />}
            </div>
          ))}
          <div className="ml-3 text-xs uppercase tracking-widest text-slate-500">Step {step} of 3 · Setup</div>
        </div>

        <div className="fa-card p-10">
          {step === 1 && (
            <div>
              <h2 className="font-serif text-3xl font-medium mb-2">Which type of public agency?</h2>
              <p className="text-slate-600 mb-8">This shapes how the platform calculates risk and applies exemption defaults.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {JURISDICTIONS.map((j) => (
                  <button
                    key={j.id}
                    data-testid={`onboarding-jurisdiction-${j.id}`}
                    onClick={() => setJurisdiction(j.id)}
                    className={`text-left p-5 border ${jurisdiction === j.id ? "border-[#0B1B3D] bg-[#0B1B3D]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <div className="font-semibold mb-1">{j.label}</div>
                    <div className="text-xs text-slate-500">{j.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-serif text-3xl font-medium mb-2">Applicable public records law</h2>
              <p className="text-slate-600 mb-8">We&apos;ll calculate statutory deadlines based on the law you select.</p>
              <div className="space-y-2 mb-6">
                {LAWS.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`onboarding-law-${l.id}`}
                    onClick={() => { setLaw(l.id); setDays(l.days); }}
                    className={`w-full text-left p-4 border ${law === l.id ? "border-[#0B1B3D] bg-[#0B1B3D]/5" : "border-slate-200 bg-white hover:border-slate-300"} flex items-center justify-between`}
                  >
                    <div>
                      <div className="font-semibold">{l.label}</div>
                      <div className="text-xs text-slate-500">Default response window: {l.days} business days</div>
                    </div>
                    {law === l.id && <CheckCircle2 className="w-4 h-4 text-[#0B1B3D]" />}
                  </button>
                ))}
              </div>
              <div>
                <label className="fa-label">Statutory response window (days)</label>
                <input data-testid="onboarding-response-days" type="number" min="1" max="60" value={days} onChange={(e) => setDays(e.target.value)} className="fa-input max-w-xs" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-serif text-3xl font-medium mb-2">Invite your team (optional)</h2>
              <p className="text-slate-600 mb-8">Add public records officers, attorneys, or IT staff. They&apos;ll receive invitation tokens.</p>
              <div>
                <label className="fa-label">Email addresses (comma or newline separated)</label>
                <textarea
                  data-testid="onboarding-invite-emails"
                  rows={5}
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  className="fa-input font-mono text-sm"
                  placeholder="attorney@city.gov&#10;clerk@city.gov"
                />
              </div>
              <div className="fa-card-muted p-4 mt-6 text-sm text-slate-600">
                <strong className="text-slate-900">What happens next:</strong> A sample FOIA case will be pre-loaded into your dashboard so your team can immediately experience the redaction and audit workflow.
              </div>
            </div>
          )}

          {err && <div className="fa-card p-3 border-red-200 bg-red-50 text-red-700 text-sm mt-6">{err}</div>}

          <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
            <button
              data-testid="onboarding-back"
              disabled={step === 1}
              onClick={() => setStep(step - 1)}
              className="fa-btn-secondary disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 3 ? (
              <button data-testid="onboarding-next" className="fa-btn-primary" onClick={() => setStep(step + 1)}>
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="onboarding-finish" disabled={busy} onClick={submit} className="fa-btn-primary">
                {busy ? "Setting up…" : "Activate workspace"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
